/**
 * Toast primitive (design system, FOUNDATION — v2).
 * Imperative `toast("...")` + a single <ToastHost/> mounted by the app shell.
 * Slide-in spring, fade-out exit (framer). Also the reduced-motion /
 * text-only rendering target of the Announcer. Class contract unchanged.
 */
import { AnimatePresence, motion } from "framer-motion";
import { createStore, useStore } from "../lib/store.js";
import { toastVariants } from "./motion.js";

export type ToastKind = "info" | "error" | "success" | "announcer";

export interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
}

const toastStore = createStore<{ items: ToastItem[] }>({ items: [] });
let nextId = 1;

export function toast(text: string, kind: ToastKind = "info", durationMs = 4200): void {
  const id = nextId++;
  toastStore.set((s) => ({ items: [...s.items, { id, text, kind }] }));
  window.setTimeout(() => dismissToast(id), durationMs);
}

export function dismissToast(id: number): void {
  toastStore.set((s) => ({ items: s.items.filter((t) => t.id !== id) }));
}

export function ToastHost() {
  const items = useStore(toastStore, (s) => s.items);
  return (
    <div className="toast-host" role="status" aria-live="polite">
      <AnimatePresence initial={false}>
        {items.map((t) => (
          <motion.div
            key={t.id}
            layout
            className={`toast${t.kind !== "info" ? ` toast--${t.kind}` : ""}`}
            variants={toastVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            onClick={() => dismissToast(t.id)}
          >
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
